import classes from '~/config/classes';
import timings from '~/config/timings';
import { dateTime } from '~/helpers/general';

/**
 * Helper functions for teasers
 * @module helpers/teasers
 */
export function checkIfActiveNonPrivate(campaigns) {
  return campaigns.active && campaigns.active.type !== 'private';
}

/**
 * Copy promo code and toggle tooltip.
 * @param {Event} event - Click event.
 * @param {String} tooltipSelector - Tooltip selector.
 */
export function handleCopy(event, tooltipSelector) {
  // Use currentTarget so we always reference the button element that has the
  // dataset + tooltip, regardless of which child element was actually clicked.
  const copyButton = event.currentTarget || event.target;
  const tooltip = copyButton?.querySelector(tooltipSelector);

  if (!copyButton) {
    return;
  }

  const code = copyButton.dataset.code;
  navigator.clipboard.writeText(code);

  if (!tooltip) {
    return;
  }

  tooltip.classList.add(classes.active);

  setTimeout(() => {
    tooltip.classList.remove(classes.active);
  }, timings.message);
}

/**
 * Update teaser content based on campaign store state (code applied or not).
 * Grid teaser usually contains multiple teasers. Update all.
 * @param {Object} campaigns Can be cnvs.discount-campaigns (localStorage) or
 * object passed in 'discount-campaign' event emitted by discount-campaigns.vue
 */
export function updateTeasers(campaigns, selectors) {
  this.$data = Object.keys(this.$data).length ? this.$data : this.data(this);

  const { promoTexts, copyButtons } = this.$data.selectors;

  if (!copyButtons) {
    return;
  }

  const copyButtonIcons = this.querySelectorAll(`${selectors.copyButton} svg`);

  if (checkIfActiveNonPrivate(campaigns)) {
    const copyButtonTexts = this.querySelectorAll(
      `${selectors.copyButton} span:first-child`,
    );

    copyButtons.forEach((copyButton) => {
      copyButton.disabled = true;
    });

    copyButtonIcons.forEach((copyButtonIcon) => {
      copyButtonIcon.style.display = 'none';
    });

    copyButtonTexts.forEach((copyButtonText) => {
      copyButtonText.innerText = campaigns.active.code[0];
    });

    promoTexts.forEach((promoText) => {
      promoText.innerText = promoText.dataset.codeApplied;
    });

    this.teasersUpdated = true;
  } else if (this.teasersUpdated) {
    // reset everything to initial state
    copyButtons.forEach((copyButton) => {
      const copyButtonText = copyButton.querySelector('span');

      copyButton.disabled = false;
      copyButtonText.innerText = copyButton.dataset.code;
    });

    copyButtonIcons.forEach((copyButtonIcon) => {
      copyButtonIcon.style.display = 'unset';
    });

    promoTexts.forEach((promoText) => {
      promoText.innerHTML = promoText.dataset.text;
    });

    this.teasersUpdated = false;
  }
}

/**
 * Update SINGLE teaser content based on campaign store state (code applied or not).
 * @param {Object} campaigns Can be cnvs.discount-campaigns (localStorage) or
 * object passed in 'discount-campaign' event emitted by discount-campaigns.vue
 */
export function updateTeaser(campaigns) {
  this.$data = Object.keys(this.$data).length ? this.$data : this.data(this);

  const { promoText, copyButton } = this.$data.selectors;

  if (!copyButton) {
    return;
  }

  const copyButtonIcon = copyButton.querySelector('svg');
  const copyButtonText = copyButton.querySelector('span:first-child');

  if (checkIfActiveNonPrivate(campaigns)) {
    copyButton.disabled = true;
    if (copyButtonIcon) copyButtonIcon.style.display = 'none';
    copyButtonText.innerText = campaigns.active.code[0];
    if (promoText) promoText.innerText = promoText.dataset.codeApplied;

    this.teaserUpdated = true;
  } else if (this.teaserUpdated) {
    // reset everything to initial state
    copyButton.disabled = false;
    copyButtonText.innerText =
      copyButton.dataset.text || copyButton.dataset.code;
    if (copyButtonIcon) copyButtonIcon.style.display = 'unset';
    if (promoText) promoText.innerText = promoText.dataset.text;

    this.teaserUpdated = false;
  }
}

/**
 * Shows current teaser banner.
 * @param {Element} teaser - Teaser element.
 * @param {Element} fallback - Fallback  element.
 */
export function showTeaser(teaser, teasers, fallback) {
  teasers.forEach((teaserImage) => {
    teaserImage.classList.add(classes.hidden);
  });

  teaser.classList.remove(classes.hidden);

  if (fallback) {
    fallback.classList.add(classes.hidden);
  }
}

/**
 * Shows teasers banner.
 * @param {Array} teasers - Teaser elements.
 * @param {Element} fallback - Fallback element.
 */
export function hideTeasers(teasers) {
  for (const teaser of teasers) {
    teaser.classList.add(classes.hidden);
  }
}

/**
 * Handle campaign view for dataLayer.
 * @param {HTMLElement} teaser - Teaser item.
 * @param {String} eventType - Event type:
 * (view_promotion or select_promotion).
 */
export function handleTrackingCampaign(
  campaign,
  name,
  eventType = 'view_promotion',
) {
  cnvs.EventBus.emit(`dataLayer:${eventType}`, {
    promotion: {
      promotion_id: campaign.dataset.campaignId
        ? campaign.dataset.campaignId
        : '',
      promotion_name: campaign.dataset.campaignName
        ? campaign.dataset.campaignName
        : '',
      creative_slot: name.replace(' ', '-'),
      creative_name: name,
    },
  });
}

/**
 * Find teaser campaign or closest to end time from current time
 * @param {Array} teasers - Teaser elements.
 * @returns {Element} - Teaser element or null.
 */
export function teaserClosestToEndTime(teasers, selectedTeaser = null) {
  let returnTeaser = null;

  if (selectedTeaser !== null) {
    const validTime = dateTime(
      selectedTeaser.dataset.start,
      selectedTeaser.dataset.end,
    );

    if (validTime) {
      returnTeaser = selectedTeaser;
    }
  }

  /**
   * Iterate over teasers to find closest to end time.
   */
  for (const teaser of teasers) {
    const validTime = dateTime(teaser.dataset.start, teaser.dataset.end);

    if (!validTime) {
      if (returnTeaser === teaser) {
        returnTeaser = null;
      }

      continue;
    }

    /**
     * Return teaser if it has no end date (never expires).
     */
    if (!teaser.dataset?.end || teaser.dataset?.end === 'false') {
      returnTeaser = teaser;
      continue;
    }

    const teaserTimeToStart = new Date(teaser.dataset.start);
    const teaserTimeToEnd = new Date(teaser.dataset.end);
    const currentTime = new Date();

    returnTeaser =
      currentTime >= teaserTimeToStart && currentTime <= teaserTimeToEnd
        ? teaser
        : null;
  }

  /**
   * Return teaser, if none found return null.
   */
  return returnTeaser;
}

/**
 * Shows fallback if all teasers are hidden.
 * @param {Array} teasers - Teaser elements.
 * @param {Element} fallback - Fallback element.
 */
export function showFallBack(teasers, fallback) {
  const allTeasersHidden = teasers.every((teaser) =>
    teaser.classList.contains('is-hidden'),
  );

  if (!allTeasersHidden) {
    return;
  }

  if (fallback) {
    fallback.classList.remove(classes.hidden);
  }
}

export function checkTeaserTimeRemaining(selectedTeaser, selectors) {
  const { fallback, teasers } = this.$data.selectors;
  const oldSelectedTeaser = selectedTeaser;
  const currentTeaser = teaserClosestToEndTime(teasers, selectedTeaser);

  if (!currentTeaser) {
    hideTeasers(teasers);
    showFallBack(teasers, fallback);
    return;
  }

  showTeaser(currentTeaser, teasers, fallback);

  if (currentTeaser === oldSelectedTeaser) {
    return;
  }

  handleTrackingCampaign(
    currentTeaser,
    'Campaign simple teaser',
    'view_promotion',
  );

  /**
   * Add click event listener after view event.
   */
  const selectedTeaserLink = currentTeaser.querySelector(selectors.teaserLink);

  if (
    !selectedTeaserLink ||
    selectedTeaserLink.classList.contains('js-hero-teaser-link')
  ) {
    return;
  }

  selectedTeaserLink.addEventListener('click', () => {
    handleTrackingCampaign(
      currentTeaser,
      'Campaign simple teaser',
      'select_promotion',
    );
  });
}
